import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';
import { useTabParam } from '../../shared/ui/useTabParam';

type Ref = { personId: string; name: string };
type Priority = 'baja' | 'media' | 'alta' | 'urgente';
type Stage = 'solicitud' | 'evaluacion' | 'autorizada' | 'en_proceso' | 'completada' | 'rechazada';
type Item = { id: string; title: string; location: string | null; priority: Priority; status: Stage; reportedBy: Ref; responsible: Ref | null; createdAt: string };
type Detail = Item & {
  description: string | null; budgetCents: number | null; actualCostCents: number | null; materials: string | null; completedAt: string | null;
  canManage: boolean; canAuthorize: boolean; next: Stage[];
  quotes: { id: string; supplier: string; amountCents: number; notes: string | null }[];
  files: { id: string; kind: 'foto' | 'evidencia' | 'cotizacion' }[];
  history: { status: Stage; note: string | null; by: Ref; at: string }[];
};

export const STAGE: Record<Stage, string> = { solicitud: 'Solicitud', evaluacion: 'En evaluación', autorizada: 'Autorizada', en_proceso: 'En proceso', completada: 'Completada', rechazada: 'Rechazada' };
const ACTION: Record<Stage, string> = { solicitud: 'Reabrir', evaluacion: 'Empezar evaluación', autorizada: 'Autorizar', en_proceso: 'Empezar trabajo', completada: 'Marcar terminado', rechazada: 'Rechazar' };
const PRIORITY: Record<Priority, string> = { baja: 'Baja', media: 'Media', alta: 'Alta', urgente: 'Urgente' };
const FLOW: Stage[] = ['solicitud', 'evaluacion', 'autorizada', 'en_proceso', 'completada'];
const money = (c: number | null) => (c === null ? '—' : formatMoney(c, 'MXN'));
const day = (iso: string) => new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' });

/** Mantenimiento: reportar algo que hay que arreglar y darle seguimiento. */
export function MaintenancePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/inventory/churches/${churchId}/maintenance`;
  const data = useAsync((signal) => (churchId ? api.get<{ canManage: boolean; items: Item[] }>(base, signal) : Promise.resolve(null)), [base]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useTabParam(['abiertos', 'terminados'] as const);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;

  async function report(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const photo = f.get('photo');
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>(base, { title: t('title'), description: t('description'), location: t('location'), priority: t('priority') });
      if (photo instanceof File && photo.size > 0) await api.upload(`${base}/${id}/files?tipo=foto`, photo, { 'x-file-name': encodeURIComponent(photo.name) });
      navigate(`/app/mantenimiento/${id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const items = data.status === 'ready' && data.data ? data.data.items : [];
  const shown = items.filter((i) => (filter === 'abiertos' ? i.status !== 'completada' && i.status !== 'rechazada' : i.status === 'completada' || i.status === 'rechazada'));
  return (
    <div className="page">
      <header className="page__header">
        <h1>Mantenimiento</h1>
        <p className="muted">¿Algo descompuesto en el templo? Repórtalo con una foto y dale seguimiento.</p>
      </header>
      {!open ? (
        <div><Button onClick={() => setOpen(true)}>Reportar algo</Button></div>
      ) : (
        <Card title="Reportar algo">
          <form className="form" onSubmit={report}>
            {error ? <Alert>{error}</Alert> : null}
            <Field label="¿Qué hay que arreglar?" name="title" required minLength={3} maxLength={150} placeholder="Gotera en el salón de niños" />
            <div className="form__row">
              <Field label="¿Dónde? (opcional)" name="location" maxLength={150} placeholder="Salón 2" />
              <div className="field">
                <label htmlFor="mt-priority">Prioridad</label>
                <select id="mt-priority" name="priority" defaultValue="media">
                  {Object.entries(PRIORITY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="mt-desc">Detalles (opcional)</label>
              <textarea id="mt-desc" name="description" rows={3} maxLength={3000} />
            </div>
            <div className="field">
              <label htmlFor="mt-photo">Foto (opcional)</label>
              <input id="mt-photo" name="photo" type="file" accept="image/*" capture="environment" />
            </div>
            <div className="actions">
              <Button type="submit" busy={busy}>Enviar reporte</Button>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            </div>
          </form>
        </Card>
      )}
      <div className="tabs" role="tablist" aria-label="Filtro">
        {(['abiertos', 'terminados'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={filter === k} className={`tabs__tab${filter === k ? ' tabs__tab--active' : ''}`} onClick={() => setFilter(k)}>
            {k === 'abiertos' ? 'Pendientes' : 'Terminados'}
          </button>
        ))}
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        shown.length === 0 ? (
          <Card><EmptyState title={filter === 'abiertos' ? 'Nada pendiente. ¡Todo en orden!' : 'Todavía no hay trabajos terminados.'} /></Card>
        ) : (
          <ul className="list card">
            {shown.map((i) => (
              <li key={i.id} className="list__item">
                <Link className="list__link" to={`/app/mantenimiento/${i.id}`}>
                  <strong>{i.title}</strong>
                  <span className="small muted">
                    {STAGE[i.status]} · Prioridad {PRIORITY[i.priority].toLowerCase()}{i.location ? ` · ${i.location}` : ''} · {day(i.createdAt)}
                    {i.responsible ? ` · Responsable: ${i.responsible.name}` : ''}
                  </span>
                </Link>
                {i.priority === 'urgente' && i.status !== 'completada' ? <span className="badge badge--warn">Urgente</span> : null}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

export function MaintenanceDetailPage() {
  const { requestId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/inventory/churches/${churchId}/maintenance`;
  const url = `${base}/${requestId}`;
  const data = useAsync((signal) => (churchId ? api.get<Detail>(url, signal) : Promise.resolve(null)), [url]);
  const team = useAsync((signal) => (churchId ? api.get<{ team: Ref[] }>(base, signal).then((r) => r.team) : Promise.resolve([])), [base]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  if (!churchId) return <EmptyState title="Elige primero una iglesia en el selector de arriba." />;
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} onRetry={data.reload} /></div>;
  const d = data.data;
  if (!d) return null;
  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const upload = (kind: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void run(() => api.upload(`${url}/files?tipo=${kind}`, file, { 'x-file-name': encodeURIComponent(file.name) }), 'Archivo agregado.');
  };
  const pics = (kind: string) => d.files.filter((f) => f.kind === kind);

  return (
    <div className="page page--narrow">
      <p className="small"><Link to="/app/mantenimiento">← Mantenimiento</Link></p>
      <header className="page__header">
        <h1>{d.title}</h1>
        <p className="muted">
          Prioridad {PRIORITY[d.priority].toLowerCase()}{d.location ? ` · ${d.location}` : ''} · Lo reportó {d.reportedBy.name} el {day(d.createdAt)}
        </p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <ol className="steps" aria-label="Avance">
        {FLOW.map((s) => (
          <li key={s} aria-current={d.status === s ? 'step' : undefined} className={FLOW.indexOf(s) <= FLOW.indexOf(d.status) ? 'steps__done' : ''}>{STAGE[s]}</li>
        ))}
      </ol>
      {d.status === 'rechazada' ? <Alert>No se autorizó este trabajo.</Alert> : null}
      {d.description ? <Card><p className="pre-line">{d.description}</p></Card> : null}

      <Card title="Fotos">
        <div className="photo-grid">
          {[...pics('foto'), ...pics('evidencia')].map((f) => (
            <figure key={f.id}>
              <img src={`/api${url}/files/${f.id}`} alt={f.kind === 'foto' ? 'Foto del problema' : 'Evidencia del trabajo'} loading="lazy" />
              <figcaption className="small muted">{f.kind === 'foto' ? 'Problema' : 'Terminado'}</figcaption>
            </figure>
          ))}
        </div>
        <div className="actions actions--wrap">
          <label className="btn btn--secondary">
            Agregar foto
            <input type="file" accept="image/*" hidden onChange={upload('foto')} />
          </label>
          {d.canManage ? (
            <label className="btn btn--secondary">
              Agregar evidencia
              <input type="file" accept="image/*" hidden onChange={upload('evidencia')} />
            </label>
          ) : null}
        </div>
      </Card>

      {d.next.length ? (
        <Card title="Siguiente paso">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const to = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('value') ?? d.next[0]!;
              const note = String(f.get('note') ?? '').trim() || null;
              if (to === 'rechazada' && !window.confirm('¿Rechazar esta solicitud de mantenimiento?')) return;
              void run(() => api.post(`${url}/stage`, { to, note }), 'Listo.');
            }}
          >
            <Field label="Nota (opcional)" name="note" maxLength={1000} />
            <div className="actions actions--wrap">
              {d.next.map((s) => (
                <Button key={s} type="submit" value={s} variant={s === 'rechazada' ? 'ghost' : 'primary'} busy={busy}>{ACTION[s]}</Button>
              ))}
            </div>
          </form>
        </Card>
      ) : null}

      <Card title="Trabajo" action={d.canManage && !editing ? <Button variant="ghost" onClick={() => setEditing(true)}>Editar</Button> : null}>
        {editing ? (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const t = (k: string) => String(f.get(k) ?? '').trim() || null;
              const m = (k: string) => (t(k) ? parseMoney(t(k)!) : null);
              void run(() => api.put(url, { title: t('title'), description: d.description, location: t('location'), priority: t('priority'), responsibleId: t('responsibleId'), budgetCents: m('budget'), actualCostCents: m('actual'), materials: t('materials') }), 'Guardado.').then((ok) => ok && setEditing(false));
            }}
          >
            <Field label="Título" name="title" defaultValue={d.title} required maxLength={150} />
            <div className="form__row">
              <Field label="Dónde" name="location" defaultValue={d.location ?? ''} maxLength={150} />
              <div className="field">
                <label htmlFor="mt-edit-priority">Prioridad</label>
                <select id="mt-edit-priority" name="priority" defaultValue={d.priority}>
                  {Object.entries(PRIORITY).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="mt-resp">Responsable</label>
              <SearchSelect id="mt-resp" name="responsibleId" defaultValue={d.responsible?.personId ?? ''} emptyLabel="Sin asignar" options={personOptions(team.status === 'ready' ? team.data : [])} />
            </div>
            <div className="form__row">
              <Field label="Presupuesto" name="budget" inputMode="decimal" defaultValue={d.budgetCents !== null ? String(d.budgetCents / 100) : ''} />
              <Field label="Costo real" name="actual" inputMode="decimal" defaultValue={d.actualCostCents !== null ? String(d.actualCostCents / 100) : ''} />
            </div>
            <div className="field">
              <label htmlFor="mt-materials">Materiales</label>
              <textarea id="mt-materials" name="materials" rows={2} maxLength={2000} defaultValue={d.materials ?? ''} />
            </div>
            <div className="actions">
              <Button type="submit" busy={busy}>Guardar</Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
            </div>
          </form>
        ) : (
          <dl className="details">
            <dt>Responsable</dt><dd>{d.responsible?.name ?? 'Sin asignar'}</dd>
            <dt>Presupuesto</dt><dd>{money(d.budgetCents)}</dd>
            <dt>Costo real</dt><dd>{money(d.actualCostCents)}</dd>
            {d.materials ? <><dt>Materiales</dt><dd className="pre-line">{d.materials}</dd></> : null}
          </dl>
        )}
      </Card>

      {d.canManage ? (
        <Card title="Cotizaciones">
          <ul className="list">
            {d.quotes.map((q) => (
              <li key={q.id} className="list__item">
                <span>{q.supplier} · <strong>{money(q.amountCents)}</strong>{q.notes ? <span className="small muted"> · {q.notes}</span> : null}</span>
                <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Quitar esta cotización?')) void run(() => api.delete(`${url}/quotes/${q.id}`), 'Cotización quitada.'); }}>Quitar</Button>
              </li>
            ))}
          </ul>
          <form
            className="family-add"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const amount = parseMoney(String(f.get('amount') ?? ''));
              if (amount === null) return setMsg({ tone: 'error', text: 'Escribe el monto de la cotización.' });
              void run(() => api.post(`${url}/quotes`, { supplier: String(f.get('supplier') ?? '').trim(), amountCents: amount, notes: String(f.get('qnotes') ?? '').trim() || null }), 'Cotización agregada.').then((ok) => ok && form.reset());
            }}
          >
            <Field label="Proveedor" name="supplier" required maxLength={120} />
            <Field label="Monto" name="amount" inputMode="decimal" required />
            <Field label="Nota" name="qnotes" maxLength={500} />
            <Button type="submit" variant="secondary" busy={busy}>Agregar</Button>
          </form>
        </Card>
      ) : null}

      <Card title="Historia">
        <ul className="list">
          {d.history.map((h, i) => (
            <li key={i} className="list__item list__item--stack">
              <span><strong>{STAGE[h.status]}</strong> <span className="small muted">· {h.by.name} · {day(h.at)}</span></span>
              {h.note ? <span className="small">{h.note}</span> : null}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
